import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/react'
import i18n from '../i18n'
import { useMaintenanceStore } from '../store/maintenanceStore'
import { isMaintenanceApiError, isReauthApiError, translateApiError } from './apiErrors'
import { withAuthRefreshLock } from './authRefreshLock'
import { getRuntimeEnv } from './runtime-env'

// ── Base URL ─────────────────────────────────────────────────────────────────
const BASE_URL = getRuntimeEnv('VITE_API_URL')!
const OFFER_REPOSITORY_BASE_URL = getRuntimeEnv('VITE_OFFER_REPOSITORY_API_URL')!
const ADS_MANAGER_BASE_URL = getRuntimeEnv('VITE_ADS_MANAGER_API_URL') ?? '/ads-manager/api/v1'

// ── Axios instances ───────────────────────────────────────────────────────────
// withCredentials: refresh cookie is httpOnly Path=/api/v1/auth — must be sent
// on login/refresh/logout. Access token still travels as Bearer for API calls.
const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
})

export const offerRepositoryApi = axios.create({
  baseURL: OFFER_REPOSITORY_BASE_URL,
  withCredentials: false,
})

export const adsManagerApi = axios.create({
  baseURL: ADS_MANAGER_BASE_URL,
  withCredentials: false,
})

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

// ── Auth callbacks (injected by authStore via initApiAuth) ────────────────────
// Avoids circular imports while keeping the store as the single source of truth.
let _getAccessToken: (() => string | null) | null = null
let _setAccessToken: ((accessToken: string, user?: unknown) => void) | null = null
let _onLogout: (() => void) | null = null

export function initApiAuth(callbacks: {
  getAccessToken: () => string | null
  setAccessToken: (accessToken: string, user?: unknown) => void
  onLogout: () => void
}) {
  _getAccessToken = callbacks.getAccessToken
  _setAccessToken = callbacks.setAccessToken
  _onLogout = callbacks.onLogout
}

function resolveAccessToken(): string | null {
  return _getAccessToken?.() ?? null
}

function applyAuthHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = resolveAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Forward the live UI language so the backend uses Accept-Language as the
  // canonical source of locale — no JWT lang claim, no staleness possible.
  config.headers['Accept-Language'] = i18n.language || 'pl'
  return config
}

// ── Token refresh queue (shared across all authenticated clients) ─────────────
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  failedQueue = []
}

/** Body/query auth — a 401 here is credentials failure, not an expired session. */
function isAuthPublicRequest(url: string | undefined): boolean {
  if (!url) return false
  return [
    '/auth/login',
    '/auth/register',
    '/auth/refresh-token',
    '/auth/session/renew',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-email',
    '/auth/google',
  ].some((path) => url.includes(path))
}

/**
 * Cookie-based renew: httpOnly refresh cookie is sent via withCredentials.
 * Bare axios — never go through an authenticated instance (would re-enter 401 handling).
 * Cross-tab lock: only one tab rotates the cookie; siblings wait then proceed.
 *
 * Path is ``/auth/session/renew`` (not ``refresh-token``) — filter lists often block
 * URLs containing ``refresh-token``, which surfaces as JWT expiry → login with zero
 * renew hits in nginx (seen for Firefox clients).
 */
export async function refreshAccessToken(): Promise<string> {
  return withAuthRefreshLock(async () => {
    const { data } = await axios.post(
      `${BASE_URL}/auth/session/renew`,
      undefined,
      { withCredentials: true },
    )
    const { access_token, user } = data
    _setAccessToken?.(access_token, user)
    return access_token as string
  })
}

function reportSessionRenewFailure(error: unknown): void {
  const status = (error as { response?: { status?: number } })?.response?.status
  const code =
    (error as { code?: string })?.code ||
    (error as { response?: { data?: { code?: string } } })?.response?.data?.code
  Sentry.captureMessage('auth.session_renew_failed', {
    level: 'warning',
    tags: {
      auth_event: 'session_renew_failed',
      http_status: status != null ? String(status) : 'network',
    },
    extra: {
      axiosCode: code,
      message: error instanceof Error ? error.message : String(error),
    },
  })
}

/**
 * Shared 401 → refresh → retry for every authenticated Axios client.
 * One refresh queue so concurrent 401s from `api` + `offerRepositoryApi` + `adsManagerApi` coalesce.
 *
 * Deploy workflow puts nginx into maintenance mode during DB migrations.
 * In that window nginx returns 503 with `{"error":"maintenance",...}` for any
 * /api/* request. We flip the maintenance store flag so a global banner can
 * render; the first successful response after the deploy clears the flag.
 */
function attachAuthResponseInterceptor(client: AxiosInstance) {
  client.interceptors.response.use(
    (response) => {
      useMaintenanceStore.getState().setActive(false)
      return response
    },
    async (error) => {
      const originalRequest = error.config as RetriableRequestConfig | undefined

      if (isMaintenanceApiError(error)) {
        useMaintenanceStore.getState().setActive(true)
        return Promise.reject(error)
      }

      if (error.response?.status !== 401 || !originalRequest) {
        return Promise.reject(error)
      }

      // Marketplace token expired (needs_reauth) — not an Ofertator JWT expiry.
      if (isReauthApiError(error)) {
        return Promise.reject(error)
      }

      // Login/register/etc. return 401 for bad credentials — do not treat as session refresh.
      if (isAuthPublicRequest(originalRequest.url)) {
        return Promise.reject(error)
      }

      // Prevent retrying the same request a second time
      if (originalRequest._retry) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest._retry = true
          originalRequest.headers.Authorization = `Bearer ${token}`
          return client(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Refresh cookie is httpOnly — always attempt; failure means no session.
        const accessToken = await refreshAccessToken()
        processQueue(null, accessToken)
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return client(originalRequest)
      } catch (refreshError) {
        reportSessionRenewFailure(refreshError)
        processQueue(refreshError)
        _onLogout?.()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    },
  )
}

// ── Request interceptors ──────────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  applyAuthHeaders(config)

  // Add trailing slash only for collection endpoints to prevent 307 redirects
  if (config.url && !config.url.endsWith('/')) {
    const [baseUrl, queryString] = config.url.split('?')
    const needsTrailingSlash = [
      '/allegro/templates',
      '/accounts',
      '/allegro/images',
      '/allegro/offers',
    ].some(endpoint => baseUrl === endpoint)

    if (needsTrailingSlash) {
      config.url = queryString ? `${baseUrl}/?${queryString}` : `${baseUrl}/`
    }
  }

  return config
})

offerRepositoryApi.interceptors.request.use((config) => applyAuthHeaders(config))
adsManagerApi.interceptors.request.use((config) => applyAuthHeaders(config))

attachAuthResponseInterceptor(api)
attachAuthResponseInterceptor(offerRepositoryApi)
attachAuthResponseInterceptor(adsManagerApi)

// ── Error utilities ───────────────────────────────────────────────────────────

/**
 * Parse FastAPI 422 Unprocessable Entity responses into a flat field → message map.
 *
 * Supports both shapes:
 *   - Classic FastAPI:  { detail: [{ loc, msg }] }
 *   - App error envelope: { code: "validation.failed", errors: [{ loc, message }] }
 */
export function parseApiFieldErrors(error: unknown): Record<string, string> {
  const data = (error as any)?.response?.data
  const items: any[] = Array.isArray(data?.detail)
    ? data.detail
    : Array.isArray(data?.errors)
      ? data.errors
      : []
  if (items.length === 0) return {}
  return Object.fromEntries(
    items
      .filter((e: any) => Array.isArray(e.loc) && e.loc.length >= 2)
      .map((e: any) => {
        const field = e.loc[e.loc.length - 1] as string
        const raw = (e.message ?? e.msg ?? '') as string
        const msg = raw.replace(/^Value error,\s*/i, '')
        return [field, msg]
      })
      .filter(([, msg]) => Boolean(msg))
  )
}

/**
 * Extract a human-readable error message from an Axios error.
 *
 * Handles FastAPI response shapes:
 *   - { code, message, params } → `errors:<code>` in the UI language
 *   - detail: string           → returned as-is
 *   - detail: [{msg, ...}]     → Pydantic v2 validation array, joined
 *   - detail: { code, ... }    → structured error, falls back to fallback
 *   - anything else            → fallback string
 */
export function getApiError(error: unknown, fallback: string): string {
  // Maintenance / bare gateway 503s — go through translateApiError so we never
  // leak Axios' "Request failed with status code 503" or nginx's English body.
  if (isMaintenanceApiError(error) || (error as any)?.response?.status === 503) {
    return translateApiError(error, i18n.t.bind(i18n))
  }
  const data = (error as any)?.response?.data
  if (data && typeof data === 'object' && typeof data.code === 'string') {
    return translateApiError(error, i18n.t.bind(i18n))
  }
  if (data && typeof data === 'object' && typeof data.message === 'string' && data.message.trim()) {
    return data.message
  }
  const detail = data?.detail
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    if (typeof detail.message === 'string' && detail.message.trim()) return detail.message
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((e: any) => (e.msg as string | undefined)?.replace(/^Value error,\s*/i, '') ?? String(e))
      .join('; ')
  }
  // apiRequest() rethrows as Error(message) — preserve that message instead of
  // collapsing to the generic fallback. Still scrub dirty Axios status strings.
  if (error instanceof Error && error.message.trim()) {
    return translateApiError(error, i18n.t.bind(i18n))
  }
  return fallback
}

/**
 * Extract a typed structured error when the backend returns `detail` as an
 * object with a `code` discriminator.
 *
 * Usage:
 *   const conflict = getStructuredApiError<ActiveSubscriptionsError>(error, 'active_subscriptions')
 */
export function getStructuredApiError<T extends { code: string }>(
  error: unknown,
  code: T['code'],
): T | null {
  const detail = (error as any)?.response?.data?.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code === code) {
    return detail as T
  }
  return null
}

/**
 * Executes an API request, unwraps response.data, and normalises any error
 * into a plain `Error` with a human-readable message.
 *
 * Use inside `mutationFn` when the caller only needs `err.message`.
 * When you need structured error detail (getStructuredApiError), call api.* directly.
 */
export async function apiRequest<T>(
  request: () => Promise<{ data: T }>,
  fallback = 'Wystąpił błąd',
): Promise<T> {
  try {
    const response = await request()
    return response.data
  } catch (err) {
    throw new Error(getApiError(err, fallback))
  }
}

export { api }
export default api
